/**
 * Input bảng (core-loop 5.1, contract r1-5): pointer events trên canvas + setPointerCapture,
 * hit-test theo toạ độ lưới (không listener trên sprite). pointermove chỉ xếp hàng điểm;
 * xử lý gộp một lần mỗi requestAnimationFrame. Giữa hai điểm lấy mẫu dày (≤ 1/4 ô) để vuốt
 * nhanh không bỏ sót ô; chỉ gọi `enter` khi điểm chạm đi VÀO vùng chạm của một ô mới.
 */
import type { Coord } from '@ml/core';

export type BoardInputHandlers = {
  /** Ô dưới điểm chạm khi pointerdown (cả ô), null nếu ngoài lưới. */
  cellAt(px: number, py: number): Coord | null;
  /** Ô có vùng chạm tròn chứa điểm, null nếu không. */
  hitCell(px: number, py: number): Coord | null;
  cellSize(): number;
  enabled(): boolean;
  down(c: Coord | null): void;
  enter(c: Coord): void;
  up(): void;
  cancel(): void;
};

export class BoardInput {
  private pointerId: number | null = null;
  private queue: { x: number; y: number }[] = [];
  private last: { x: number; y: number } | null = null;
  private current: Coord | null = null;
  private raf = 0;
  /** Số lần xử lý move (theo frame) — cho debug/kiểm hiệu năng. */
  frames = 0;
  moveEvents = 0;

  constructor(
    private readonly el: HTMLElement,
    private readonly h: BoardInputHandlers,
  ) {
    el.addEventListener('pointerdown', this.onDown);
    el.addEventListener('pointermove', this.onMove);
    el.addEventListener('pointerup', this.onUp);
    el.addEventListener('pointercancel', this.onCancel);
    el.addEventListener('lostpointercapture', this.onLost);
    el.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  private local(e: PointerEvent): { x: number; y: number } {
    const r = this.el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private onDown = (e: PointerEvent): void => {
    e.preventDefault();
    if (this.pointerId !== null || !e.isPrimary || !this.h.enabled()) return;
    const p = this.local(e);
    const c = this.h.cellAt(p.x, p.y);
    this.h.down(c);
    if (!c) return;
    this.pointerId = e.pointerId;
    try {
      this.el.setPointerCapture(e.pointerId);
    } catch {
      // Sự kiện tổng hợp (test) không có pointer thật để capture.
    }
    this.last = p;
    this.current = this.h.hitCell(p.x, p.y) ?? c;
    this.queue = [];
  };

  private onMove = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointerId) return;
    e.preventDefault();
    this.moveEvents++;
    const list = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [];
    if (list.length > 0) for (const ce of list) this.queue.push(this.local(ce));
    else this.queue.push(this.local(e));
    if (!this.raf) this.raf = requestAnimationFrame(this.flush);
  };

  private flush = (): void => {
    this.raf = 0;
    if (this.pointerId === null || !this.last) {
      this.queue = [];
      return;
    }
    this.frames++;
    const step = Math.max(2, this.h.cellSize() / 4);
    for (const p of this.queue) {
      const a = this.last;
      const dx = p.x - a.x;
      const dy = p.y - a.y;
      const n = Math.max(1, Math.ceil(Math.hypot(dx, dy) / step));
      for (let i = 1; i <= n; i++) this.sample(a.x + (dx * i) / n, a.y + (dy * i) / n);
      this.last = p;
    }
    this.queue = [];
  };

  private sample(x: number, y: number): void {
    const c = this.h.hitCell(x, y);
    if (c && (!this.current || c.x !== this.current.x || c.y !== this.current.y)) {
      this.h.enter(c);
    }
    this.current = c;
  }

  private onUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointerId) return;
    e.preventDefault();
    if (this.queue.length) this.queue.push(this.local(e));
    else this.queue = [this.local(e)];
    if (this.raf) cancelAnimationFrame(this.raf);
    this.flush();
    this.end();
    this.h.up();
  };

  private onCancel = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointerId) return;
    this.end();
    this.h.cancel();
  };

  private onLost = (e: PointerEvent): void => {
    // Mất capture mà không có pointerup (ví dụ hệ thống chen ngang) → huỷ.
    if (e.pointerId !== this.pointerId) return;
    this.end();
    this.h.cancel();
  };

  private end(): void {
    const id = this.pointerId;
    this.pointerId = null;
    this.last = null;
    this.current = null;
    this.queue = [];
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (id !== null) {
      try {
        if (this.el.hasPointerCapture(id)) this.el.releasePointerCapture(id);
      } catch {
        /* bỏ qua */
      }
    }
  }
}
